import React, { Fragment, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import {
    Typography, Divider, List, ListItem,
    ListItemText, ListItemIcon, Avatar,
    Link,
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import clsx from 'clsx';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';

import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Icon from '@mdi/react';
import {
    mdiMotion, mdiSpeedometer,
    mdiLocationExit, mdiLocationEnter
} from '@mdi/js';
import eventIcons from '../../utils/eventIcons.js';

import {
    setHighlightedDrive, removeHighlightedDrive, highlightedDriveSelector
} from '../../features/datacenter/historySlice.js';
import { setNeedBounding } from '../../features/map/mapSlice';


const useStyles = makeStyles((theme) => ({
    root: {
        maxWidth: 345,
    },
    expand: {
        transform: 'rotate(0deg)',
        marginLeft: 'auto',
        transition: useTheme().transitions.create('transform', {
            duration: useTheme().transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
}));

const TimeLineDriveItem = ({ data, track }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };
    const oneDay = moment(data.stopStartTime).format('D/M/Y') === moment(data.stopEndTime).format('D/M/Y');

    const highlightedDrive = useSelector(highlightedDriveSelector);
    const thisDriveHighlighted = highlightedDrive.startIndex === data.startIndex && highlightedDrive.endIndex === data.endIndex;

    const highlightDriveCallback = () => {
        if (thisDriveHighlighted) {
            dispatch(removeHighlightedDrive());
        } else {
            //console.log("Hioghliogh drive", data.startIndex);
            dispatch(setHighlightedDrive({
                startIndex: data.startIndex,
                endIndex: data.endIndex,
            }))
            dispatch(setNeedBounding(true));
        }
    }

    return (
        <Card className={classes.root} style={{ backgroundColor: thisDriveHighlighted ? 'Beige' : 'White' }}>
            <CardHeader
                title={
                    <Typography varant="body1">
                        <Link href="#" onClick={highlightDriveCallback}>
                            <b><span style={{ color: 'ForestGreen' }}>Drive: </span></b>
                        </Link>
                        {data.driveDistance && data.driveDistance.toLocaleString('en-IN', { maximumFractionDigits: 1 })} Km
                    </Typography>
                }
                subheader={
                    <Fragment>
                        <Typography varant="subtitle2" color="textSecondary" component="span">
                            <b>Run Time</b>: {data.driveDuration}
                        </Typography>
                        <br />
                        {
                            data.events.length > 0 &&
                            <Typography variant="subtitle2" color="textSecondary" component="span">
                                <b>Events: </b> {data.events.length}
                            </Typography>
                        }
                    </Fragment>
                }
                action={
                    <IconButton
                        className={clsx(classes.expand, {
                            [classes.expandOpen]: expanded,
                        })}
                        onClick={handleExpandClick}
                        aria-expanded={expanded}
                        aria-label="show more"
                        size="large">
                        <ExpandMoreIcon />
                    </IconButton>
                }
                avatar={
                    <Avatar style={{ backgroundColor: 'white' }}>
                        <Icon
                            path={mdiMotion}
                            title="Drive"
                            size={1}
                            color="ForestGreen"
                        />
                    </Avatar>
                }
            />
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Divider />
                    <List dense>
                        <ListItem>
                            <ListItemIcon>
                                <Avatar style={{ backgroundColor: 'White' }}>
                                    <Icon
                                        path={mdiSpeedometer}
                                        title="Speed"
                                        size={0.8}
                                        color="DodgerBlue"
                                    />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Fragment>
                                        <b>Average Speed: </b> {data.averageSpeed && data.averageSpeed.toLocaleString('en-IN', { maximumFractionDigits: 1 })} Km/h
                                    </Fragment>
                                }
                                secondary={
                                    <Fragment>
                                        <b>Top Speed</b>: {data.topSpeed && data.topSpeed.toLocaleString('en-IN', { maximumFractionDigits: 1 })} Km/h
                                    </Fragment>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Avatar style={{ backgroundColor: 'White' }}>
                                    <Icon
                                        path={mdiLocationExit}
                                        title="Start"
                                        size={0.8}
                                        color="Green"
                                    />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Fragment>
                                        <b>Started: </b> {`${moment(data.driveStartTime).format('Do MMM YY hh:mm A')}`}
                                    </Fragment>
                                }
                                secondary={
                                    <Fragment>
                                        {track[data.startIndex].address ? track[data.startIndex].address.net_addr : ''}
                                    </Fragment>
                                }
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Avatar style={{ backgroundColor: 'White' }}>
                                    <Icon
                                        path={mdiLocationEnter}
                                        title="End"
                                        size={0.8}
                                        color="Salmon"
                                    />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Fragment>
                                        <b>Ended: </b> {`${moment(data.driveEndTime).format('Do MMM YY hh:mm A')}`}
                                    </Fragment>
                                }
                                secondary={
                                    <Fragment>
                                        {track[data.endIndex].address ? track[data.endIndex].address.net_addr : ''}
                                    </Fragment>
                                }
                            />
                        </ListItem>
                        {
                            data.events.length > 0 &&
                            <Fragment>
                                {
                                    data.events.map(e =>
                                        <ListItem key={e.event_id}>
                                            <ListItemIcon>
                                                <Avatar style={{ backgroundColor: 'White' }}>
                                                    {eventIcons(e.event_type || e.event_desc)}
                                                </Avatar>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Fragment>
                                                        <b>{e.event_type || e.event_desc}</b> at {moment(e.event_time).format(oneDay ? 'h:mm A' : 'D/M/Y h:mm A')}
                                                    </Fragment>
                                                }
                                                secondary={track[e.routeIndex].address ? track[e.routeIndex].address.fmtd_addr : ''}
                                            />
                                        </ListItem>
                                    )
                                }
                            </Fragment>
                        }
                    </List>
                </CardContent>
            </Collapse>

        </Card>
    );
}

export default TimeLineDriveItem;